import React from "react";
import type {ContractDetail} from "../contractExplorerTypes.ts";
import {documentsById} from "../contractExplorerFormat.ts";
import {BundleFacts} from "./BundleFacts.tsx";
import {ContractLinesGrid} from "./ContractLinesGrid.tsx";
import {MissingInformation} from "./MissingInformation.tsx";
import {OtherAmounts} from "./OtherAmounts.tsx";
import {PartiesSection} from "./PartiesSection.tsx";
import {SourceFocusContext, scrollToSource} from "./sourceFocus.ts";
import {SourcesSection} from "./SourcesSection.tsx";
import {TermsSection} from "./TermsSection.tsx";
import {VersionHistory} from "./VersionHistory.tsx";
import {VersionPicker} from "./VersionPicker.tsx";

// How long the Documents list takes to open before a `[n]` can scroll to it.
const OPEN_MS = 300;

/**
 * The bundle read whole: what it is missing, then its parties, periods and
 * documents (closed until wanted), then one version of it
 * (the one in force unless another is picked) with its metadata, terms,
 * other amounts and lines. Mounted per bundle, so the picked version starts over on another one.
 */
export const DetailsTab: React.FC<{contract: ContractDetail}> = ({contract}) => {
    const [versionNo, setVersionNo] = React.useState<number | null>(contract.version_in_force);
    const [documentsOpen, setDocumentsOpen] = React.useState(false);
    const version = contract.versions.find(v => v.version_no === versionNo) ?? contract.versions.at(-1);
    const documents = React.useMemo(() => documentsById(contract.documents), [contract.documents]);

    const focusSource = React.useCallback((position: number) => {
        if (documentsOpen) {
            scrollToSource(position);
            return;
        }
        setDocumentsOpen(true);
        window.setTimeout(() => scrollToSource(position), OPEN_MS);
    }, [documentsOpen]);

    return (
        <SourceFocusContext.Provider value={focusSource}>
            <MissingInformation gaps={contract.gaps}/>
            <div className="ci-collapses">
                <PartiesSection parties={contract.parties} linkage={contract.linkage} documents={documents}/>
                <VersionHistory versions={contract.versions} inForce={contract.version_in_force}
                                shown={version?.version_no ?? -1} documents={documents} onShow={setVersionNo}/>
                <SourcesSection bagId={contract.databag_id} contractId={contract.id} documents={contract.documents}
                                expanded={documentsOpen} onChange={setDocumentsOpen}/>
            </div>
            {version && contract.versions.length > 1 && (
                <div className="ci-version-bar">
                    <VersionPicker versions={contract.versions} value={version.version_no}
                                   inForce={contract.version_in_force} onChange={setVersionNo}/>
                    <span className="ci-sub">
                        {version.version_no === contract.version_in_force
                            ? `The version in force on ${contract.as_of}.`
                            : 'Values marked changed differ from the version before.'}
                    </span>
                </div>
            )}
            <BundleFacts contract={contract} version={version} documents={documents}/>
            <TermsSection contract={contract} version={version} documents={documents}/>
            <OtherAmounts documents={contract.documents} byId={documents}/>
            <ContractLinesGrid contract={contract} version={version} documents={documents}/>
        </SourceFocusContext.Provider>
    );
};
